import React, { useState, useEffect } from "react";
import { Container, Row, Col, Input, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { API_URL } from "../../../config";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

const EditCurrencyList = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [currencySingleData, setCurrencySingleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchSingleCurrency = async () => {
    try {
      setLoading(true);
      const resp = await fetch(API_URL + "/api/wallet/getCurrencyById", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ _id: id }),
      });
      const data = await resp.json();
      if (data.status && data.message) {
        setCurrencySingleData(data.message);
      } else {
        console.error("Invalid response data", data);
      }
    } catch (error) {
      console.error("Error fetching currency list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchSingleCurrency();
    }
  }, [id]);

  useEffect(() => {
    if (currencySingleData) {
      formik.setValues({
        currencyName: currencySingleData.currencyName || "",
        currencySymbol: currencySingleData.currencySymbol || "",
        currencySymbolCode: currencySingleData.currencySymbolCode || "",
        curnType: currencySingleData.curnType || "",
        basecoin: currencySingleData.basecoin || "",
        decimal: currencySingleData.decimal || "",
        contractAddress: currencySingleData.contractAddress || "",
        apiid: currencySingleData.apiid || "",
        depositEnable: String(currencySingleData.depositEnable) || "",
        withdrawEnable: String(currencySingleData.withdrawEnable) || "",
        minWalletTransfer: currencySingleData.minWalletTransfer || "",
        transferEnable: String(currencySingleData.transferEnable) || "",

        tagContent: currencySingleData.tagContent || "",
        minCurDeposit: currencySingleData.minCurDeposit || "",
        depositNotes: currencySingleData.depositNotes || "",
        withdrawNotes: currencySingleData.withdrawNotes || "",
        USDvalue: currencySingleData.USDvalue || "",
        status: String(currencySingleData.status) || "",
        siteDecimal: currencySingleData.siteDecimal || "",
        image: null,
      });
      setPreviewURL(currencySingleData.image || null);
    }
  }, [currencySingleData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setPreviewURL(fileURL);
      formik.setFieldValue("image", file);
    }
  };

  const submitFormData = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "image" && values[key]) {
          formData.append(key, values[key]);
        } else if (
          values[key] !== undefined &&
          values[key] !== null &&
          key !== "image"
        ) {
          formData.append(key, values[key]);
        }
      });
      formData.append("_id", id);

      const resp = await fetch(`${API_URL}/api/wallet/updateCurrency`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });
      const data = await resp.json();
      if (resp.ok && data.status === true) {
        toast.success("Currency Updated successfully!");
        navigate("/currency-list");
        setPreviewURL(null);
      } else {
        toast.error(data.message || "Something went wrong!");
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Server error, please try again!");
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      currencyName: "",
      currencySymbol: "",
      currencySymbolCode: "",
      curnType: "",
      basecoin: "",
      decimal: "",
      contractAddress: "",
      apiid: "",
      depositEnable: "",
      withdrawEnable: "",
      minWalletTransfer: "",
      transferEnable: "",
      tagContent: "",
      minCurDeposit: "",
      depositNotes: "",
      withdrawNotes: "",
      USDvalue: "",
      status: "",
      siteDecimal: "",
      image: null,
    },
    validationSchema: Yup.object({
      currencyName: Yup.string().required("Currency Name is required"),
      currencySymbol: Yup.string().required("Symbol is required"),
      currencySymbolCode: Yup.string().required("Symbol Code is required"),
      curnType: Yup.string().required("Currency Type is required"),
      basecoin: Yup.string().required("Base Coin is required"),
      decimal: Yup.number()
        .typeError("Must be a number")
        .required("Decimal Point is required"),
      contractAddress: Yup.string().optional(),
      apiid: Yup.string().required("API ID or NA is required"),
      depositEnable: Yup.string().required("Deposit Status is required"),
      withdrawEnable: Yup.string().required("Withdraw Status is required"),
      minWalletTransfer: Yup.number()
        .typeError("Must be a number")
        .required("Minimum Wallet Transfer is required"),
      transferEnable: Yup.string().required("P2P Wallet status is required"),
      tagContent: Yup.string().optional(),
      minCurDeposit: Yup.number()
        .typeError("Must be a number")
        .required("Minimum Deposit is required"),
      depositNotes: Yup.string().required("Deposit Notes is required"),
      withdrawNotes: Yup.string().required("Withdraw Notes is required"),
      USDvalue: Yup.number()
        .typeError("Must be a number")
        .required("USD Value is required"),
      status: Yup.string().required("Status is required"),
      siteDecimal: Yup.number()
        .typeError("Must be a number")
        .required("Site Decimal is required"),
      image: Yup.mixed().notRequired(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      await submitFormData(values);
      setSubmitting(false);
    },
  });

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0 font-size-18">Edit Currency List</h4>
            </div>
          </Col>
        </Row>
        {loading && (
          <div className="text-center my-3">
            <Spinner size="lg" />
          </div>
        )}
        <form onSubmit={formik.handleSubmit}>
          <div className="grid-2 pt-4 pb-4">
            <div>
              <label>Currency Name</label>
              <Input
                type="text"
                name="currencyName"
                value={formik.values.currencyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Currency Name"
                className="form-control"
                readOnly
              />
              {formik.touched.currencyName && formik.errors.currencyName && (
                <small className="text-danger">
                  {formik.errors.currencyName}
                </small>
              )}
            </div>
            <div>
              <label>Symbol</label>
              <Input
                type="text"
                name="currencySymbol"
                value={formik.values.currencySymbol}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Symbol"
                className="form-control"
                readOnly
              />
              {formik.touched.currencySymbol &&
                formik.errors.currencySymbol && (
                  <small className="text-danger">
                    {formik.errors.currencySymbol}
                  </small>
                )}
            </div>
            <div>
              <label>Symbol Code</label>
              <Input
                type="text"
                name="currencySymbolCode"
                value={formik.values.currencySymbolCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Symbol Code"
                className="form-control"
              />
              {formik.touched.currencySymbolCode &&
                formik.errors.currencySymbolCode && (
                  <small className="text-danger">
                    {formik.errors.currencySymbolCode}
                  </small>
                )}
            </div>
            <div>
              <label>Currency Type (Crypto / Fiat)</label>
              <select
                name="curnType"
                value={formik.values.curnType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Currency Type</option>
                <option value="Crypto">Crypto</option>
                <option value="Fiat">Fiat</option>
              </select>
              {formik.touched.curnType && formik.errors.curnType && (
                <small className="text-danger">{formik.errors.curnType}</small>
              )}
            </div>
            <div>
              <label>Base Coin (if not Token)</label>
              <select
                name="basecoin"
                value={formik.values.basecoin}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Base Coin</option>
                <option value="ERC20">ERC20</option>
                <option value="TRC20">TRC20</option>
                <option value="BEP20">BEP20</option>
                <option value="Coin">Coin</option>
              </select>
              {formik.touched.basecoin && formik.errors.basecoin && (
                <small className="text-danger">{formik.errors.basecoin}</small>
              )}
            </div>
            <div>
              <label>Decimal Point</label>
              <Input
                type="number"
                style={{
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "textfield",
                }}
                name="decimal"
                value={formik.values.decimal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Decimal"
                className="form-control"
              />
              {formik.touched.decimal && formik.errors.decimal && (
                <small className="text-danger">{formik.errors.decimal}</small>
              )}
            </div>
            <div>
              <label>Contract Address</label>
              <Input
                type="text"
                name="contractAddress"
                value={formik.values.contractAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Contract Address"
                className="form-control"
              />
              {formik.touched.contractAddress &&
                formik.errors.contractAddress && (
                  <small className="text-danger">
                    {formik.errors.contractAddress}
                  </small>
                )}
            </div>
            <div>
              <label>
                Coingekko API ID (or NA). Example: FBT token API ID is
                Exchangepro-exchange
              </label>
              <Input
                type="text"
                name="apiid"
                value={formik.values.apiid}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter API ID"
                className="form-control"
              />
              {formik.touched.apiid && formik.errors.apiid && (
                <small className="text-danger">{formik.errors.apiid}</small>
              )}
            </div>
            <div>
              <label>Deposit Status</label>
              <select
                name="depositEnable"
                value={formik.values.depositEnable}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Status</option>
                <option value="1">Active</option>
                <option value="0">DeActive</option>
              </select>
              {formik.touched.depositEnable && formik.errors.depositEnable && (
                <small className="text-danger">
                  {formik.errors.depositEnable}
                </small>
              )}
            </div>
            <div>
              <label>Withdraw Status</label>
              <select
                name="withdrawEnable"
                value={formik.values.withdrawEnable}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Status</option>
                <option value="1">Active</option>
                <option value="0">DeActive</option>
              </select>
              {formik.touched.withdrawEnable &&
                formik.errors.withdrawEnable && (
                  <small className="text-danger">
                    {formik.errors.withdrawEnable}
                  </small>
                )}
            </div>
            <div>
              <label>Minimum Wallet Transfer</label>
              <Input
                type="number"
                name="minWalletTransfer"
                value={formik.values.minWalletTransfer}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Min Wallet Transfer"
                className="form-control"
              />
              {formik.touched.minWalletTransfer &&
                formik.errors.minWalletTransfer && (
                  <small className="text-danger">
                    {formik.errors.minWalletTransfer}
                  </small>
                )}
            </div>
            <div>
              <label>P2P Wallet Enable</label>
              <select
                name="transferEnable"
                value={formik.values.transferEnable}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Status</option>
                <option value="1">Active</option>
                <option value="0">DeActive</option>
              </select>
              {formik.touched.transferEnable &&
                formik.errors.transferEnable && (
                  <small className="text-danger">
                    {formik.errors.transferEnable}
                  </small>
                )}
            </div>
            <div>
              <label>Deposit Tag Content</label>
              <Input
                type="text"
                name="tagContent"
                value={formik.values.tagContent}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Deposit Tag Content"
                className="form-control"
              />
              {formik.touched.tagContent && formik.errors.tagContent && (
                <small className="text-danger">
                  {formik.errors.tagContent}
                </small>
              )}
            </div>
            <div>
              <label>Min Deposit</label>
              <Input
                type="number"
                name="minCurDeposit"
                value={formik.values.minCurDeposit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Min Deposit"
                className="form-control"
              />
              {formik.touched.minCurDeposit && formik.errors.minCurDeposit && (
                <small className="text-danger">
                  {formik.errors.minCurDeposit}
                </small>
              )}
            </div>
            <div>
              <label>Deposit Notes</label>
              <Input
                type="text"
                name="depositNotes"
                value={formik.values.depositNotes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Deposit Notes"
                className="form-control"
              />
              {formik.touched.depositNotes && formik.errors.depositNotes && (
                <small className="text-danger">
                  {formik.errors.depositNotes}
                </small>
              )}
            </div>
            <div>
              <label>Withdraw Notes</label>
              <Input
                type="text"
                name="withdrawNotes"
                value={formik.values.withdrawNotes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Withdraw Notes"
                className="form-control"
              />
              {formik.touched.withdrawNotes && formik.errors.withdrawNotes && (
                <small className="text-danger">
                  {formik.errors.withdrawNotes}
                </small>
              )}
            </div>
            <div>
              <label>USD Value</label>
              <Input
                type="number"
                name="USDvalue"
                value={formik.values.USDvalue}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="USD Value"
                className="form-control"
              />
              {formik.touched.USDvalue && formik.errors.USDvalue && (
                <small className="text-danger">{formik.errors.USDvalue}</small>
              )}
            </div>
            <div>
              <label>Status</label>
              <select
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Status</option>
                <option value="1">Active</option>
                <option value="0">DeActive</option>
              </select>
              {formik.touched.status && formik.errors.status && (
                <small className="text-danger">{formik.errors.status}</small>
              )}
            </div>
            <div>
              <label>Site Decimal</label>
              <Input
                type="number"
                name="siteDecimal"
                value={formik.values.siteDecimal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Site Decimal"
                className="form-control"
              />
              {formik.touched.siteDecimal && formik.errors.siteDecimal && (
                <small className="text-danger">
                  {formik.errors.siteDecimal}
                </small>
              )}
            </div>
            <div>
              <label>Image</label>
              <Input
                type="file"
                className="form-control"
                onChange={handleFileChange}
                accept="image/*"
              />
              {previewURL && (
                <div className="mt-3">
                  <p>Preview:</p>
                  <img
                    src={previewURL}
                    alt="Preview"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      borderRadius: "5px",
                      border: "1px solid #ccc",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="d-r-g-3 mt-3">
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={loading}
            >
              {loading ? <Spinner size="sm" /> : "Update Currency"}
            </button>
          </div>
        </form>
      </Container>
    </div>
  );
};

export default EditCurrencyList;
